import { useMemo } from 'react';
import { Chess, PieceSymbol } from 'chess.js';

interface MaterialAdvantage {
    whiteAdvantage: number;
    blackAdvantage: number;
    capturedWhitePieces: PieceSymbol[]; // Pieces white has lost (black has captured)
    capturedBlackPieces: PieceSymbol[]; // Pieces black has lost (white has captured)
}

const PIECE_VALUES: Record<PieceSymbol, number> = {
    p: 1,
    n: 3,
    b: 3,
    r: 5,
    q: 9,
    k: 0
};

const STANDARD_INITIAL_PIECES: Record<PieceSymbol, number> = {
    p: 8,
    n: 2,
    b: 2,
    r: 2,
    q: 1,
    k: 1
};

export function useMaterialAdvantage(fen: string): MaterialAdvantage {
    return useMemo(() => {
        const whitePieces: Record<PieceSymbol, number> = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
        const blackPieces: Record<PieceSymbol, number> = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };

        let whiteMaterial = 0;
        let blackMaterial = 0;

        try {
            const chess = new Chess(fen === 'start' ? 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1' : fen);
            const board = chess.board();

            for (let r = 0; r < 8; r++) {
                for (let c = 0; c < 8; c++) {
                    const piece = board[r][c];
                    if (piece) {
                        if (piece.color === 'w') {
                            whitePieces[piece.type]++;
                            whiteMaterial += PIECE_VALUES[piece.type];
                        } else {
                            blackPieces[piece.type]++;
                            blackMaterial += PIECE_VALUES[piece.type];
                        }
                    }
                }
            }
        } catch (e) {
            // Invalid fen, fallback to 0
        }

        const capturedWhitePieces: PieceSymbol[] = [];
        const capturedBlackPieces: PieceSymbol[] = [];

        // Calculate what pieces are missing
        const pieceTypes: PieceSymbol[] = ['p', 'n', 'b', 'r', 'q'];
        for (const pt of pieceTypes) {
            const missingWhite = STANDARD_INITIAL_PIECES[pt] - whitePieces[pt];
            for (let i = 0; i < missingWhite; i++) {
                capturedWhitePieces.push(pt);
            }

            const missingBlack = STANDARD_INITIAL_PIECES[pt] - blackPieces[pt];
            for (let i = 0; i < missingBlack; i++) {
                capturedBlackPieces.push(pt);
            }
        }

        // Sort them for visually pleasing display: Queen -> Rook -> Bishop -> Knight -> Pawn
        const sortOrder: Record<PieceSymbol, number> = { q: 1, r: 2, b: 3, n: 4, p: 5, k: 6 };
        capturedWhitePieces.sort((a, b) => sortOrder[a] - sortOrder[b]);
        capturedBlackPieces.sort((a, b) => sortOrder[a] - sortOrder[b]);

        const whiteAdvantage = Math.max(0, whiteMaterial - blackMaterial);
        const blackAdvantage = Math.max(0, blackMaterial - whiteMaterial);

        return {
            whiteAdvantage,
            blackAdvantage,
            capturedWhitePieces,
            capturedBlackPieces
        };
    }, [fen]);
}
